import {
  Box,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { Clock } from 'iconsax-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { EntityType, GlobalSearchItem, RecentSearchItem } from '@core/types/search';
import { hasValue } from '@core/utils/equality';
import { SEARCH_DROPDOWN_MAX_WIDTH } from '@web/constants/app';

import { ENTITY_CONFIGS } from '../EntitySearchInput/entityConfig';
import { EntitySearchResults } from '../EntitySearchInput/EntitySearchResults';
import { EntitySearchTabs } from '../EntitySearchInput/EntitySearchTabs';
import type { GlobalSearchState } from './useGlobalSearch';

const DropdownContainer = styled(Box)(({ theme }) => ({
  maxWidth: SEARCH_DROPDOWN_MAX_WIDTH,
  maxHeight: 480,
  overflowY: 'auto',
  backgroundColor: theme.palette.SlabGray[0],
}));

const RecentRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 16px',
  cursor: 'pointer',
  gap: 12,
  '&:hover': {
    backgroundColor: theme.palette.SlabGray.Table,
  },
}));

type Props = {
  anchorEl: HTMLElement | null;
  search: GlobalSearchState;
  onClose: () => void;
};

export const GlobalSearchDropdown: FC<Props> = ({ anchorEl, search, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { hasQuery } = search;

  const recentSearches = useMemo(
    () => (search.isOpen ? search.getRecentItems() : []),
    [search.isOpen, search.getRecentItems],
  );

  const handleSelectResult = (type: EntityType, item: GlobalSearchItem): void => {
    const url = `${ENTITY_CONFIGS[type].routePrefix}/${item.id}`;
    search.addRecentView({ id: item.id, type, name: ENTITY_CONFIGS[type].getName(item), url });
    onClose();
    navigate(url);
  };

  const handleSelectRecent = (item: RecentSearchItem): void => {
    search.addRecentView(item);
    onClose();
    navigate(item.url);
  };

  return (
    <Popover
      open={search.isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            minWidth: '320px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.SlabGray.Stroke}`,
            boxShadow: '0 3px 10px rgba(96,108,128,0.06)',
          },
        },
      }}
    >
      <DropdownContainer>
        {hasQuery && (
          <EntitySearchTabs
            entityTypes={search.entityTypes}
            activeTab={search.activeTab}
            onChange={search.setActiveTab}
            getCount={search.getCount}
            totalCount={search.totalCount}
          />
        )}

        {hasQuery && !search.isLoading && !search.isError && hasValue(search.searchResults) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
            }}
          >
            <Typography sx={{ fontSize: 18, color: theme.palette.SlabGray[600] }}>
              Results
            </Typography>
            <Box
              role='button'
              tabIndex={0}
              onClick={() => search.clearQuery()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  search.clearQuery();
                }
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
            >
              <Clock size={18} variant='TwoTone' color={theme.palette.SlabIndigo[700]} />
              <Typography sx={{ fontSize: 18, color: theme.palette.SlabIndigo[700] }}>
                Recently Viewed
              </Typography>
            </Box>
          </Box>
        )}

        {hasQuery && search.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} sx={{ color: theme.palette.SlabIndigo[500] }} />
          </Box>
        )}

        {hasQuery && !search.isLoading && search.isError && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: theme.palette.SlabGray[600], fontSize: 14 }}>
              Search is temporarily unavailable. Please try again.
            </Typography>
          </Box>
        )}

        {hasQuery && !search.isLoading && !search.isError && hasValue(search.searchResults) && (
          <EntitySearchResults
            results={search.searchResults}
            entityTypes={search.entityTypes}
            activeTab={search.activeTab}
            onSelect={handleSelectResult}
            onViewAll={(type): void => {
              navigate(ENTITY_CONFIGS[type].routePrefix);
              onClose();
            }}
          />
        )}

        {!hasQuery && (
          <Box sx={{ p: 2 }}>
            {recentSearches.length > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1,
                    pb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: theme.palette.SlabGray[600],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Recently Viewed
                  </Typography>
                  <IconButton
                    size='small'
                    aria-label='Close recently viewed'
                    onClick={onClose}
                    sx={{ color: theme.palette.SlabGray[600], p: 0.25 }}
                  >
                    <Box
                      component='span'
                      sx={{ fontSize: 16, lineHeight: 1, fontWeight: 400, display: 'flex' }}
                    >
                      ✕
                    </Box>
                  </IconButton>
                </Box>
                {recentSearches.map((item) => {
                  const config = ENTITY_CONFIGS[item.type];
                  const { Icon } = config;
                  return (
                    <RecentRow
                      key={item.id}
                      role='button'
                      tabIndex={0}
                      onClick={() => handleSelectRecent(item)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSelectRecent(item);
                        }
                      }}
                    >
                      <Icon size={24} variant='TwoTone' color={theme.palette.SlabGray[600]} />
                      <Typography sx={{ fontSize: 14, color: theme.palette.SlabGray[900] }}>
                        {item.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.SlabGray[600],
                          ml: 'auto',
                        }}
                      >
                        {config.label}
                      </Typography>
                    </RecentRow>
                  );
                })}
              </>
            ) : (
              <Typography
                sx={{
                  color: theme.palette.SlabGray[600],
                  fontSize: 14,
                  textAlign: 'center',
                  py: 2,
                }}
              >
                Start typing to search across all entities
              </Typography>
            )}
          </Box>
        )}
      </DropdownContainer>
    </Popover>
  );
};
